import { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature as topojsonFeature } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';

// A handful of otherwise real, undisputed sovereign states carry a "-99" placeholder
// ISO2 in this Natural Earth dataset (a documented data-quality gap in the source
// itself, persists across its 110m/50m resolutions, not something this project
// introduced). Same finite, exact correction the Phase 2 country-eligibility
// migration already applies on the database side -- not fuzzy name matching.
const FEATURE_ISO2_OVERRIDES: Record<string, string> = {
  France: 'FR',
  Norway: 'NO',
  Taiwan: 'TW', // this dataset uses the non-standard "CN-TW" for Taiwan's ISO_A2
};

interface CountryFeature {
  type: 'Feature';
  properties: { ISO_A2?: string; ADMIN?: string; NAME?: string };
  geometry: unknown;
}

interface CountryFeatureCollection {
  type: 'FeatureCollection';
  features: CountryFeature[];
}

interface TinyCountryPoint {
  type: 'Feature';
  properties: { ADMIN?: string; ISO_A2?: string };
  geometry: { type: 'Point'; coordinates: [number, number] };
}

interface TinyCountryCollection {
  type: 'FeatureCollection';
  features: TinyCountryPoint[];
}

const resolveIso2 = (properties: { ISO_A2?: string; ADMIN?: string }): string | null => {
  const raw = properties.ISO_A2;
  if (raw && raw !== '-99') return raw;
  const admin = properties.ADMIN;
  return admin ? FEATURE_ISO2_OVERRIDES[admin] ?? null : null;
};

const featureLabel = (feature: CountryFeature): string =>
  feature.properties.ADMIN || feature.properties.NAME || 'Unknown';

interface PassportWorldMapProps {
  highlightedIso2: Set<string>;
  destinationCountsByIso2?: Map<string, number>;
  onSelectCountry?: (iso2: string, name: string) => void;
}

const WIDTH = 960;
const HEIGHT = 500;
// The rendered viewBox crops a bit off the top (Arctic) and more off the
// bottom (Antarctica, disproportionately large at this projection's extreme
// southern latitudes) -- no surfed-country content lives near either pole,
// so this makes the map read as more vertically efficient without changing
// the projection math or any path data. Norway (the northernmost country in
// the Passport-eligible list) stays comfortably inside this crop.
const VIEWBOX_Y_MIN = 20;
const VIEWBOX_HEIGHT = 425;

const PassportWorldMap = ({ highlightedIso2, destinationCountsByIso2, onSelectCountry }: PassportWorldMapProps) => {
  const [features, setFeatures] = useState<CountryFeature[] | null>(null);
  const [tinyCountries, setTinyCountries] = useState<TinyCountryPoint[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hovered, setHovered] = useState<{ iso2: string; name: string; x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    // /geo/countries-50m.json is TopoJSON (quantized, topology-shared -- roughly the
    // same gzipped size as the 110m GeoJSON it replaces despite covering 242
    // countries instead of 177), converted to GeoJSON features at fetch time via
    // topojson-client. A property-trim-then-flatten GeoJSON approach was tried first
    // and discarded: reducing point density with topojson-simplify silently
    // corrupted several countries' geometry (rings collapsed to duplicate points --
    // caught by visually rendering the result, not just checking feature counts),
    // and flattening a quantized topology back to full-precision GeoJSON server-side
    // produced a *larger* file than the original source. Shipping the topology
    // itself and letting topojson-client expand it in the browser is the actual
    // correct way to get TopoJSON's size benefit without either problem.
    fetch('/geo/countries-50m.json')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((topology: Topology) => {
        const collection = topojsonFeature(
          topology,
          topology.objects.countries as GeometryCollection
        ) as unknown as CountryFeatureCollection;
        if (!cancelled) setFeatures(collection.features);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the map.');
      });

    // Companion point layer (Natural Earth's own purpose-built dataset for country
    // that don't read as a visible area at world-map scale, e.g. Malta, Barbados,
    // Seychelles) -- best-effort only; if it fails to load, the country polygons
    // above still render and remain fully functional, just without the marker aid.
    fetch('/geo/tiny-countries-50m.json')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: TinyCountryCollection | null) => {
        if (!cancelled && data) setTinyCountries(data.features);
      })
      .catch(() => {
        /* non-critical, silently skip markers */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const projection = useMemo(
    () => d3.geoNaturalEarth1().fitSize([WIDTH, HEIGHT], { type: 'Sphere' } as never),
    []
  );
  const pathGenerator = useMemo(() => d3.geoPath(projection), [projection]);

  if (loadError) {
    return (
      <div className="rounded-md border border-border/60 bg-card/50 px-4 py-16 text-center text-sm text-muted-foreground">
        {loadError}
      </div>
    );
  }

  if (!features) {
    return <div className="aspect-[960/425] w-full animate-pulse rounded-md bg-secondary/60" aria-hidden="true" />;
  }

  // Surfed tiny-island countries get a small marker layered on their (real, but
  // often only a few pixels wide) polygon, so they're never invisible on the map
  // purely because of their physical size. Purely a visual aid: the underlying
  // country polygon remains the sole interactive/accessible element, so this
  // doesn't create a second, confusing target for keyboard or screen-reader users.
  const surfedTinyMarkers = tinyCountries
    .map((f) => {
      const iso2 = resolveIso2(f.properties);
      if (!iso2 || !highlightedIso2.has(iso2)) return null;
      const projected = projection(f.geometry.coordinates);
      if (!projected) return null;
      return { iso2, x: projected[0], y: projected[1] };
    })
    .filter((m): m is { iso2: string; x: number; y: number } => m !== null);

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        viewBox={`0 ${VIEWBOX_Y_MIN} ${WIDTH} ${VIEWBOX_HEIGHT}`}
        role="group"
        aria-label="World map. Countries you've surfed are highlighted in terracotta."
        className="h-auto w-full"
      >
        {features.map((feature, idx) => {
          const iso2 = resolveIso2(feature.properties);
          const name = featureLabel(feature);
          const isSurfed = !!iso2 && highlightedIso2.has(iso2);
          const d = pathGenerator(feature as never) ?? undefined;
          const count = iso2 ? destinationCountsByIso2?.get(iso2) : undefined;
          const label = isSurfed
            ? `${name}, surfed${count ? `, ${count} destination${count === 1 ? '' : 's'}` : ''}`
            : `${name}, not yet surfed`;

          const select = () => {
            if (isSurfed && iso2 && onSelectCountry) onSelectCountry(iso2, name);
          };

          return (
            <path
              key={idx}
              d={d}
              stroke="hsl(var(--background))"
              strokeWidth={0.5}
              role={isSurfed ? 'button' : undefined}
              tabIndex={isSurfed ? 0 : -1}
              aria-label={label}
              onClick={select}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  select();
                }
              }}
              onMouseEnter={(e) => {
                if (!isSurfed) return;
                const rect = containerRef.current?.getBoundingClientRect();
                setHovered({ iso2: iso2 as string, name, x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) });
              }}
              onFocus={(e) => {
                if (!isSurfed || !iso2) return;
                // Use the focused path's own real rendered position, not a fixed
                // SVG-viewBox-space guess -- the SVG is scaled down on narrow
                // viewports, so a hardcoded coordinate overflows a mobile-width
                // container.
                const pathRect = e.currentTarget.getBoundingClientRect();
                const containerRect = containerRef.current?.getBoundingClientRect();
                setHovered({
                  iso2,
                  name,
                  x: pathRect.left + pathRect.width / 2 - (containerRect?.left ?? 0),
                  y: pathRect.top + pathRect.height / 2 - (containerRect?.top ?? 0),
                });
              }}
              onMouseLeave={() => setHovered(null)}
              onBlur={() => setHovered(null)}
              style={{ cursor: isSurfed ? 'pointer' : 'default', outline: 'none' }}
              className={
                // Unsurfed fill is a deliberately darker step than the raw
                // --secondary token (same hue/saturation, ~13pt less
                // lightness) -- at --secondary's own value, unsurfed
                // countries were nearly indistinguishable from the page
                // background. Visited countries stay on the real --primary
                // token, unchanged.
                (isSurfed ? 'fill-primary ' : 'fill-[hsl(37,27%,72%)] ') +
                'transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring'
              }
            />
          );
        })}

        {surfedTinyMarkers.map((m) => (
          <circle
            key={m.iso2}
            cx={m.x}
            cy={m.y}
            r={3}
            className="fill-primary"
            stroke="hsl(var(--background))"
            strokeWidth={1}
            aria-hidden="true"
            pointerEvents="none"
          />
        ))}
      </svg>

      {hovered && (
        <div
          className="pointer-events-none absolute z-10 max-w-[160px] rounded-md border border-border bg-card px-3 py-1.5 text-xs shadow-md"
          style={{
            // Defensive clamp so the tooltip can never push the page into
            // horizontal overflow, regardless of viewport width.
            left: `clamp(8px, ${hovered.x + 12}px, calc(100% - 150px))`,
            top: Math.max(8, hovered.y - 12),
          }}
        >
          <span className="font-medium text-foreground">{hovered.name}</span>
          {destinationCountsByIso2?.get(hovered.iso2) !== undefined && (
            <span className="ml-1 text-muted-foreground">
              · {destinationCountsByIso2.get(hovered.iso2)} destination
              {destinationCountsByIso2.get(hovered.iso2) === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PassportWorldMap;
